/**
 * Izba Admin — Metrics Config Pipeline runs (view-only).
 *
 * Mirrors Data Hub `PipelineRunsTab`: expandable run list with When / Source /
 * Duration / Status / Phases and Phase 1 / Phase 2 detail. Uses shared Table.
 */
import React, { useEffect, useMemo, useState } from "react";

import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";

import Button from "../../Components/Button";
import Table from "../../Components/Table";
import { SyntheticBadge } from "../../data-hub-library/components/DatasetPill";
import {
  DATASET_LABELS,
  type DatasetKind,
} from "../../data-hub-library/data/dataset";
import {
  RO_SCORES_ENABLED,
  isRoScoresPhase,
} from "../../data-hub-library/data/flags";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchDataHubPipelineRuns,
  type DataHubPhaseResult,
  type DataHubPipelineRun,
  type DataHubPhaseStatus,
  type DataHubRunStatus,
} from "../../Store/Reducers/DataHubSlice";

const PHASE_2_STEPS = new Set([
  "benchmark",
  "normalize",
  "summarize",
  "kpi_scores",
  "ro_scores",
]);

const RUN_STATUS_STYLES: Record<DataHubRunStatus, string> = {
  succeeded: "bg-green-100 text-green-800",
  partial: "bg-amber-100 text-amber-900",
  failed: "bg-red-100 text-red-800",
  running: "bg-blue-100 text-blue-800",
};

const PHASE_STATUS_STYLES: Record<DataHubPhaseStatus, string> = {
  success: "text-green-700",
  failed: "text-red-700",
  skipped: "text-gray-400",
  running: "text-blue-700",
};

const PHASE_LABELS: Record<string, string> = {
  file: "File",
  columns: "Columns",
  accounts: "Accounts",
  metrics: "Metrics",
  categories: "Categories",
  import: "Import",
  qc: "QC",
  cleanse: "QC",
  heal: "Heal",
  promote: "Promote",
  commit: "Promote",
  benchmark: "Benchmark",
  normalize: "Normalize",
  summarize: "Summarize",
  kpi_scores: "Category Scores",
  ro_scores: "R&O Scores",
};

function detailFor(p: DataHubPhaseResult): { text: string; muted: boolean } {
  const bits: string[] = [];
  const e = (p.extras ?? {}) as Record<string, any>;

  if (typeof e.sheet === "string") bits.push(`Source: ${e.sheet}`);
  if (typeof e.columns === "number") bits.push(`${e.columns} columns`);

  (
    [
      ["auto_matched", "auto-matched"],
      ["user_matched", "user-matched"],
      ["ignored", "ignored"],
      ["skipped", "skipped"],
    ] as const
  ).forEach(([key, label]) => {
    if (typeof e[key] === "number") bits.push(`${e[key]} ${label}`);
  });

  if (typeof e.rows_reviewed === "number")
    bits.push(`${e.rows_reviewed} reviewed`);
  if (typeof e.rows_fixed === "number") bits.push(`${e.rows_fixed} fixed`);

  if (p.count != null && bits.length === 0) {
    bits.push(`count: ${p.count.toLocaleString()}`);
  } else if (p.count != null) {
    bits.unshift(`count: ${p.count.toLocaleString()}`);
  }

  if (p.error) bits.push(p.error);
  if (p.reason) bits.push(p.reason);

  if (bits.length === 0) {
    return { text: "No detail recorded for this step.", muted: true };
  }
  return { text: bits.join(" · "), muted: false };
}

function formatDuration(ms: number | null | undefined) {
  if (ms == null) return "—";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function formatWhen(iso: string | null | undefined) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function runStatusStyle(status: string | undefined) {
  const key = (status || "running") as DataHubRunStatus;
  return RUN_STATUS_STYLES[key] || "bg-gray-100 text-gray-700";
}

function phaseStatusStyle(status: string | undefined) {
  const key = (status || "skipped") as DataHubPhaseStatus;
  return PHASE_STATUS_STYLES[key] || "text-gray-500";
}

function phaseGroup(p: DataHubPhaseResult): "phase_1" | "phase_2" {
  if (p.group === "phase_1" || p.group === "phase_2") return p.group;
  return PHASE_2_STEPS.has(p.phase) ? "phase_2" : "phase_1";
}

function RunPhasesDetail({ run }: { run: DataHubPipelineRun }) {
  const phases = (run.phases || []).filter(
    p => RO_SCORES_ENABLED || !isRoScoresPhase(p.phase)
  );
  if (!phases.length) {
    return <div className="text-xs text-gray-500">No phase detail.</div>;
  }

  return (
    <div className="-mx-4 -my-4 divide-y divide-gray-200">
      {(["phase_1", "phase_2"] as const).map(group => {
        const inGroup = phases.filter(p => phaseGroup(p) === group);
        if (!inGroup.length) return null;
        return (
          <div key={group}>
            <div className="bg-gray-100 px-4 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              {group === "phase_1" ? "Phase 1" : "Phase 2"}
            </div>
            {inGroup.map((p, i) => {
              const detail = detailFor(p);
              return (
                <div
                  key={`${p.phase}-${i}`}
                  className="flex flex-col gap-1 px-4 py-2 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className="min-w-[150px] text-sm font-medium text-gray-900">
                      {PHASE_LABELS[p.phase] ?? p.phase}
                    </span>
                    <span
                      className={`text-xs font-semibold ${phaseStatusStyle(p.status)}`}
                    >
                      {p.status}
                    </span>
                  </div>
                  <div
                    className={`text-xs ${
                      detail.muted ? "italic text-gray-400" : "text-gray-600"
                    }`}
                  >
                    {detail.text}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function PipelineRunsPanel({
  orgId,
  orgName,
  datasetKey,
  dataset,
}: {
  orgId: string;
  orgName: string;
  datasetKey: number;
  dataset: DatasetKind;
}) {
  const dispatch = useAppDispatch();
  const {
    data: runs,
    loading,
    error,
  } = useAppSelector(s => s.dataHub.pipelineRuns);
  const [expandedKeys, setExpandedKeys] = useState<Array<string | number>>([]);

  useEffect(() => {
    setExpandedKeys([]);
    void dispatch(fetchDataHubPipelineRuns(orgId));
  }, [dispatch, orgId, datasetKey]);

  const toggleExpand = (id: string) => {
    setExpandedKeys(prev => (prev.includes(id) ? [] : [id]));
  };

  const columns = useMemo(
    () => [
      {
        key: "expand",
        title: "",
        width: "48px",
        render: (_: unknown, record: DataHubPipelineRun) => {
          const id = String(record.id);
          const isOpen = expandedKeys.includes(id);
          return (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={isOpen ? "Collapse run" : "Expand run"}
              onClick={e => {
                e.stopPropagation();
                toggleExpand(id);
              }}
            >
              {isOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          );
        },
      },
      {
        key: "run_no",
        title: "Run",
        width: "90px",
        render: (_: unknown, record: DataHubPipelineRun) => (
          <span className="font-medium text-gray-900">
            #{record.run_no ?? "—"}
          </span>
        ),
      },
      {
        key: "started_at",
        title: "When",
        width: "180px",
        render: (_: unknown, record: DataHubPipelineRun) => (
          <span className="text-gray-600">
            {formatWhen(record.started_at || record.created_at)}
          </span>
        ),
      },
      {
        key: "source",
        title: "Source",
        width: "160px",
        render: (value: string | null | undefined) => (
          <span className="text-gray-600">{value ?? "—"}</span>
        ),
      },
      {
        key: "dataset_kind",
        title: "Dataset",
        width: "120px",
        render: (_: unknown, record: DataHubPipelineRun) =>
          record.dataset_kind === "synthetic" ? (
            <SyntheticBadge dataset={record.dataset_kind} />
          ) : (
            <span className="text-gray-600">production</span>
          ),
      },
      {
        key: "duration_ms",
        title: "Duration",
        width: "110px",
        render: (value: number | null | undefined) => (
          <span className="text-gray-600">{formatDuration(value)}</span>
        ),
      },
      {
        key: "status",
        title: "Status",
        width: "120px",
        render: (value: string | undefined) => (
          <span
            className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold capitalize ${runStatusStyle(value)}`}
          >
            {value || "—"}
          </span>
        ),
      },
      {
        key: "phases",
        title: "Phases",
        width: "90px",
        textalign: "right" as const,
        render: (_: unknown, record: DataHubPipelineRun) => {
          const phases = record.phases || [];
          const ok = phases.filter(p => p.status === "success").length;
          return (
            <span className="text-gray-600">
              {ok}/{phases.length}
            </span>
          );
        },
      },
    ],
    [expandedKeys]
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="m-0 text-base font-semibold text-gray-900">
            Pipeline runs
          </h3>
          <p className="m-0 text-xs text-gray-500">
            {orgName}
            {` · ${DATASET_LABELS[dataset]}`}
            {runs.length ? ` · ${runs.length} recorded` : ""}
            {" · "}
            View-only. Start / resume / edit stays in Data Hub.
          </p>
        </div>
        <Button
          type="button"
          variant="outline-primary"
          disabled={loading}
          onClick={() => void dispatch(fetchDataHubPipelineRuns(orgId))}
          icon={
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          }
        >
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm">
        <Table
          className="[&_th]:normal-case [&_th]:tracking-normal"
          columns={columns}
          data={runs}
          loading={loading && !runs.length}
          rowKey={record => String(record.id)}
          expandedRowKeys={expandedKeys}
          expandedRowRender={record => <RunPhasesDetail run={record} />}
          emptyContent={
            <p className="text-sm text-gray-500">
              No {DATASET_LABELS[dataset].toLowerCase()} runs recorded for this
              organization yet.
            </p>
          }
          scrollable
          maxHeight="calc(100vh - 22rem)"
        />
      </div>
    </div>
  );
}

export default PipelineRunsPanel;
